/// 会員一覧の entity store (U10)。
///
/// 会員画面 (`pages/site-members`) だけが購読する。一覧取得とローディング / エラー状態のみを
/// 持ち、判断は持たない (FSD: entities は store 担当)。社内担当者の `memberListStore`
/// (`entities/member`) とは別物。

import type { SiteMemberPage } from '@domain/site-member/model';
import { parseSiteMemberPage } from '@domain/site-member/parseSiteMember';
import { ApiError } from '@shared/api/client';
import { listSiteMembers } from '@shared/api/site-members';

/// 1 ページの件数。API の既定値と同じ 50。走査窓 (先頭 1000 件) のもとで 20 ページ分にあたる。
export const SITE_MEMBERS_PER_PAGE = 50;

/// 取得失敗を利用者向けの日本語に置き換える。
///
/// backend のメッセージをそのまま画面に出さないための門。特に 503 は「その環境にまだ
/// 会員テナントが無い」という**設計どおりの状態** (prod が該当) で、英語の
/// "service temporarily unavailable: member tenant is not configured" を出すと障害に見える。
export function siteMemberListErrorMessage(err: unknown): string {
  const status = err instanceof ApiError ? err.status : null;
  switch (status) {
    case 403:
      // UI ミラー (canViewSiteMembers) を抜けて到達した場合の保険。
      return '会員一覧を表示する権限がありません。';
    case 503:
      return 'この環境ではまだ会員基盤が有効になっていません。';
    case 400:
      // 走査窓の超過。Pagination の maxPage で送れないようにしてあるので通常は到達しない。
      return '表示できる範囲 (先頭 1000 件) を超えました。';
    default:
      // 画面内に再試行の導線は置かない (一覧画面の家風)。文言も、この画面で実際に取れる
      // 行動 (開き直す) に合わせる — 押すものが無いのに「お試しください」と促さない。
      return '会員一覧を取得できませんでした。時間をおいて画面を開き直してください。';
  }
}

interface ListState {
  data: SiteMemberPage | null;
  loading: boolean;
  error: string | null;
}

const listState = $state<ListState>({ data: null, loading: false, error: null });
let listToken = 0;

export const siteMemberListStore = {
  get data(): SiteMemberPage | null {
    return listState.data;
  },
  get loading(): boolean {
    return listState.loading;
  },
  get error(): string | null {
    return listState.error;
  },
  /// `page` は API と同じ 0 起点。連打したときの古い応答は token で捨てる。
  async refresh(page: number): Promise<void> {
    const token = ++listToken;
    listState.loading = true;
    listState.error = null;
    try {
      const raw = await listSiteMembers(page, SITE_MEMBERS_PER_PAGE);
      if (token !== listToken) return;
      listState.data = parseSiteMemberPage(raw);
    } catch (err) {
      if (token !== listToken) return;
      listState.error = siteMemberListErrorMessage(err);
    } finally {
      if (token === listToken) listState.loading = false;
    }
  },
};
